import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteGroupAlias } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { DirectoryGroup } from '../../../../../types';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import SharedDialog from '../../../../shared/shared-dialog';
import Typography from '@mui/material/Typography';

interface DeleteGroupAliasDialogProps {
  group: DirectoryGroup;
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  groupEmail: string;
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function DeleteGroupAliasDialog({
  open,
  handleClose,
  selectedRows,
  groupEmail,
  group,
  changeLogRoles,
  setSelectedRows,
}: DeleteGroupAliasDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteGroupAlias(groupEmail, 'Group', selectedRows, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: selectedRows.length > 25 ? 'info' : 'success',
        message:
          selectedRows.length > 25
            ? 'The task to delete the group alias(es) has begun.'
            : 'Group alias(es) deleted successfully!',
      });

      if (selectedRows.length <= 25) {
        const currentAliases = group.aliases || [];
        const newList = currentAliases.filter(
          (alias) => !selectedRows.includes(alias)
        );

        queryClient.setQueryData([UserKeys.DirectoryGroup, group.email], {
          ...group,
          aliases: newList,
        });
      }
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the group alias(es). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.delete_group_alias, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.DeleteGroupAliases.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title={
          selectedRows.length > 1
            ? 'Delete Group Aliases'
            : 'Delete Group Alias'
        }
        dialogContent={
          <Typography>
            {selectedRows.length > 1 ? (
              <>
                Delete <strong>{selectedRows.length}</strong> aliases?
              </>
            ) : (
              <>
                Delete alias <strong>{selectedRows[0]}</strong>?
              </>
            )}
          </Typography>
        }
        primaryButton={{
          title: selectedRows.length > 1 ? 'Delete Aliases' : 'Delete Alias',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending,
          color: 'error',
        }}
      />
      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={mutate}
          mutateLoading={isPending}
        />
      )}
    </>
  );
}
